import { string, date, object, boolean, SchemaOf } from 'yup';
import dayjs from 'dayjs';

import { REQUIRED_FIELD, MAX_STRING_LENGTH_YUP_TEMPLATE, DATE_IN_PAST, 
  DATE_AFTER_CONTRACT_END_PLUS_1, 
  DATE_AFTER_DNR_DATE } from '@/config/text.const';
import { DateType } from '@/components/common/DatePicker/DatePicker';

export const errorMessages = {
  NO_FURTHER_BILLING_AFTER_DNR: DATE_AFTER_DNR_DATE,
  NO_FURTHER_BILLING_AFTER_CONTRACT_END: DATE_AFTER_CONTRACT_END_PLUS_1
};

export type DnrContractLinesFormFields = {
  reasonCode: string;
  dnrDate: Date;
  comment: string;
  noFurtherBilling: boolean;
  noFurtherBillingDate?: DateType;
};

export const validationSchema: SchemaOf<DnrContractLinesFormFields> = object().shape({
  reasonCode: string()
    .required(REQUIRED_FIELD),

  dnrDate: date()
    .min(new Date(new Date().setHours(0, 0, 0, 0)), DATE_IN_PAST)
    .required(REQUIRED_FIELD)
    .test((value, { options, createError }) => {
      const contractRenewableTerm = options?.context?.contractRenewableTerm ?? null;
      const contractEndDate = dayjs(options?.context?.contractEndDate ?? null);
      const selectedDNRDate = dayjs(value ?? null);

      if (!contractRenewableTerm && selectedDNRDate.isAfter(contractEndDate.add(1, 'day'), 'date')) {
        return createError({
          message: errorMessages.NO_FURTHER_BILLING_AFTER_CONTRACT_END
        });
      }
      return true;
    }),

  comment: string()
    .max(100, MAX_STRING_LENGTH_YUP_TEMPLATE)
    .required(REQUIRED_FIELD),

  noFurtherBilling: boolean()
    .required(),

  noFurtherBillingDate: date()
    .test((value, { createError }) => {
      const selectedDate = dayjs(value ?? null);

      if (selectedDate.isBefore(dayjs(), 'date')) {
        return createError({
          message: DATE_IN_PAST
        });
      }

      return true;
    })
    .test((value, { options, createError }) => {
      const contractEndDate = dayjs(options?.context?.contractEndDate ?? null);
      const selectedDate = dayjs(value ?? null);
      const contractRenewableTerm = options?.context?.contractRenewableTerm ?? null;

      if (!contractRenewableTerm && selectedDate.isAfter(contractEndDate, 'date')) {
        return createError({
          message: errorMessages.NO_FURTHER_BILLING_AFTER_CONTRACT_END
        });
      }

      return true;
    })
    .test((value, { parent, createError }) => {
      if (!parent?.dnrDate) {
        return true;
      }

      const selectedDate = dayjs(value ?? null);
      const dnrDate = dayjs(parent.dnrDate);

      if (selectedDate.isAfter(dnrDate, 'date')) {
        return createError({
          message: errorMessages.NO_FURTHER_BILLING_AFTER_DNR
        });
      }

      return true;
    })
    .notRequired()
});
